<template>
  <div class="soar-container flex flex-col gap-y-4">
    <h1 class="text-xl md:text-2xl text-primary font-medium border-b border-brand-gray-300">
      {{ $t('userPreferences') }}
    </h1>
    <div class="w-full flex justify-center">
      <Form
        v-if="!isLoading"
        :initial-values="profileSettings"
        :keep-values="true"
        class="w-full md:w-2/3 flex flex-col gap-3"
        v-slot="{ resetForm }"
        @submit="(values) => onSubmit(values, resetForm)"
      >
        <fieldset class="border-2 border-primary rounded p-4 flex flex-col gap-y-5">
          <legend class="px-4 text-lg md:text-xl">{{ $t('WhatsYourPreferredLanguage') }}</legend>
          <div class="flex flex-col gap-y-1">
            <label for="english" class="flex gap-x-2 accent-primary">
              <Field :value="0" id="english" name="choosePreferredLanguage" type="radio" />
              {{ $t('English') }}
            </label>
            <label for="arabic" class="flex gap-x-2 accent-primary">
              <Field :value="1" id="arabic" name="choosePreferredLanguage" type="radio" />
              {{ $t('Arabic') }}
            </label>
          </div>
        </fieldset>
        <fieldset
          :key="key"
          v-for="(category, key) in preferenceMenu"
          class="border-2 border-primary rounded p-4 flex flex-col gap-y-1"
        >
          <legend class="px-4 text-lg capitalize">
            {{ category.text }} {{ $t('pleaseSelectAllWhichApplies') }}
          </legend>
          <div :key="ley" v-for="(option, ley) in category.options" class="flex flex-col gap-4">
            <Field
              :value="true"
              :unchecked-value="false"
              :name="`${category.text}-${option.text.toLowerCase().replaceAll(' ', '-')}`"
              type="checkbox"
              v-slot="{ field }"
            >
              <label>
                <input
                  type="checkbox"
                  name="terms"
                  v-bind="field"
                  class="accent-primary"
                  @change="handleChange(option, field.checked)"
                />
                {{ option.text }}
              </label>
            </Field>
          </div>
        </fieldset>
        <div class="w-full flex justify-end gap-x-4">
          <button
            class="text-center rounded-md bg-brand-gray-600/80 hover:bg-brand-gray-600 flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
            @click.prevent="resetForm"
          >
            <div class="p-2 bg-brand-gray-600 rounded-s-md">
              <faTimes class="w-4 fill-white"></faTimes>
            </div>
            <span class="w-full text-center">
              {{ $t('Cancel') }}
            </span>
            <div class="p-0.5"></div>
          </button>
          <button
            class="text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
          >
            <div class="p-2 bg-primary rounded-s-md">
              <faDiskette class="w-4 fill-white"></faDiskette>
            </div>
            <span class="w-full text-center">
              {{ $t('Save') }}
            </span>
            <div class="p-0.5"></div>
          </button>
        </div>
      </Form>
    </div>
  </div>
</template>

<script setup>
import faTimes from '@/assets/icons/faTimes.svg?component'

import faDiskette from '@/assets/icons/faDiskette.svg?component'
import client_lookups from '@/assets/json/clientLookups.json'
import axiosInstance from '@/services'
import { useLookupsStore } from '@/stores/lookups'
import { notify } from 'notiwind'
import { Field, Form } from 'vee-validate'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const router = useRouter()
const i18n = useI18n()
const lookupsStore = useLookupsStore()
const isLoading = ref(true)

const categories = ref()
const profileSettings = ref()
const userPreferences = ref()
const selectedPreferences = ref([])

const {
  lookupUserProfileCategories: { mainUserProfileCategory, preferredLanguageForCommination }
} = client_lookups

const preferenceMenu = computed(() => {
  if (isLoading.value || !categories.value || !profileSettings.value) return

  return categories.value
    .filter((c) => c.categoryId != 2)
    .map((c) => {
      const options = profileSettings.value.filter((s) => s.categoryId == c.id)
      return {
        ...c,
        options: options.sort((a, b) => a.id > b.id)
      }
    })
    .sort((c, d) => c.id > d.id)
})

onMounted(async () => {
  categories.value = await lookupsStore.fetchLookup('/api/lookup/getUserProfileCategories')
  categories.value = categories.value.filter(
    (c) => [mainUserProfileCategory, preferredLanguageForCommination].includes(c.id) === false
  )
  profileSettings.value = await lookupsStore.fetchLookup('/api/lookup/getUserProfileSettings')
  profileSettings.value = profileSettings.value.filter(
    (p) => mainUserProfileCategory != p.categoryId
  )

  userPreferences.value = await axiosInstance.get('/api/userProfile/getUserPreferences')

  isLoading.value = false
})

const handleChange = (option, isChecked) => {
  const prefExists = selectedPreferences.value.some((sp) => sp.id == option.id)

  if (prefExists) {
    selectedPreferences.value = selectedPreferences.value.filter((sp) => sp.id != option.id)
    return
  }

  selectedPreferences.value.push({ ...option, checked: isChecked })
}

const onSubmit = async (form) => {
  const body = [
    ...selectedPreferences.value,
    profileSettings.value.find((p) => p.id == form.choosePreferredLanguage)
  ]

  await axiosInstance.post('/api/userProfile/addPreferences', body)

  notify(
    {
      group: 'success',
      title: i18n.t('SuccessUserPreferencesUpdate')
    },
    6000
  )

  router.push('/')
}
</script>

<style lang="scss" scoped></style>
